import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { BundleInfo, CapacitorUpdater } from '@capgo/capacitor-updater';

import { uuid } from '@utilities/uuid';

import { handleResponse } from './handleResponse';

const { REACT_APP_API_URL: apiUrl = '' } = process.env;

export const getApiUrl = () => apiUrl;

const getUnauthenicatedUserId = () => {
  const storedValue = localStorage.getItem('Unauthenticated-UserId');

  if (storedValue) {
    return storedValue;
  }

  const value = uuid();
  localStorage.setItem('Unauthenticated-UserId', value);
  return value;
};

export const currentCapacitorPath: { value?: string } = {};

export let currentCapGoBundle: BundleInfo | undefined;
export let currentAppVersion: string | undefined;
const getAppVersion = async () => {
  if (currentAppVersion) {
    return currentAppVersion;
  }

  const res = await CapacitorUpdater.current();
  currentCapGoBundle = res.bundle;
  currentAppVersion = res.bundle.version;
};
getAppVersion();

export let currentBuild: AppInfo | undefined;
const getBuild = async () => {
  try {
    if (currentBuild) {
      return currentBuild;
    }
    const build = await App.getInfo();
    return build;
  } catch (err) {}
};

export let currentChannel: string | undefined;
const getCurrentChannel = async () => {
  try {
    if (currentChannel) {
      return currentChannel;
    }

    const result = await CapacitorUpdater.getChannel();
    return result.channel;
  } catch (e) {}
};
getCurrentChannel();

const getHeaders = async () => {
  const defaultHeaders: RequestInit['headers'] = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'API-Version': '1.1',
    'App-Version': currentAppVersion,
    'App-Platform': Capacitor.getPlatform(),
    'Unauthenticated-UserId': getUnauthenicatedUserId(),
    'Current-Capacitor-Path': currentCapacitorPath.value,
  };

  const currentBuild = await getBuild();
  if (currentBuild) {
    defaultHeaders['App-BuildNumber'] = currentBuild.build;
    defaultHeaders['App-BuildVersion'] = currentBuild.version;
  }

  if (currentCapGoBundle) {
    defaultHeaders['Capgo-Bundle'] = JSON.stringify(currentCapGoBundle);
  }

  defaultHeaders['App-Channel'] = await getCurrentChannel();

  const token = await localStorage.getItem('token');

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  return defaultHeaders;
};

const API = {
  get: async <ResponseBody>(url: string): Promise<ResponseBody> => {
    const headers = await getHeaders();

    const response = await fetch(`${apiUrl}${url}`, {
      headers,
    });

    return handleResponse(response);
  },
  post: async <ResponseBody extends {}, RequestBody extends {}>(
    url: string,
    payload: RequestBody,
  ): Promise<ResponseBody> => {
    const headers = await getHeaders();
    const body = JSON.stringify(payload);

    const response = await fetch(`${apiUrl}${url}`, {
      method: 'POST',
      headers,
      body,
    });

    return handleResponse(response);
  },
  put: async <ResponseBody extends {}, RequestBody extends {}>(
    url: string,
    payload: RequestBody,
  ): Promise<ResponseBody> => {
    const headers = await getHeaders();
    const body = JSON.stringify(payload);

    const response = await fetch(`${apiUrl}${url}`, {
      method: 'PUT',
      headers,
      body,
    });

    return handleResponse(response);
  },

  delete: async <ResponseBody extends {}, RequestBody extends {}>(
    url: string,
    payload?: RequestBody,
  ): Promise<ResponseBody> => {
    const headers = await getHeaders();
    const body = JSON.stringify(payload);

    const response = await fetch(`${apiUrl}${url}`, {
      method: 'DELETE',
      headers,
      body,
    });

    return handleResponse(response);
  },
};

export default API;
